// callVorgangZuweisen.ts — „Bestehendem Vorgang zuweisen" aus einem Anruf.
//
// Das Gegenstück zu `callVorgangFlow.ts` (dort entsteht ein NEUER Vorgang):
// hier wird der Anruf an einen Vorgang gehängt, den es schon gibt. Beide sitzen
// hinter demselben Knopf „Vorgang" und unterscheiden sich nur im Menüpunkt.
//
// Warum eine eigene Datei statt ein paar Zeilen im Dialog: Die Regel, WANN der
// Erledigt-Stempel mitgeht, ist die einzige nicht offensichtliche Entscheidung
// des ganzen Wegs — sie gehört an eine Stelle und unter Test.
//
// Seit 01.10.2026 kann ein Anruf an mehreren Vorgängen hängen: „Zuweisen"
// fügt hinzu (`POST calls/:id/zuordnungen`), Lösen geht je Zuordnung.

import {
    telephonyService,
    type CallLogDto,
    type CallZuordnungZiel,
} from "../../services/telephonyService";
import { kontaktAbgleichAnbieten } from "./kontaktInVorgangAbgleich";

/**
 * Anruf weiteren Vorgängen/To-dos ZUORDNEN (PO 01.10.2026: ein Anruf kann an
 * mehreren hängen) und das aktualisierte DTO zurückgeben, damit Liste und
 * Detail sofort nachziehen.
 *
 * Entscheidung 59 gilt weiter: Wer einen Anruf einem Vorgang zuordnet, hat ihn
 * abgearbeitet — `resolve` stempelt ihn als erledigt. Aber NUR, wenn noch kein
 * Stempel steht (der Server prüft das ebenfalls): sonst stünde im Detail
 * „Erledigt am … von …" mit falschem Namen.
 *
 * Kennt ein Zielvorgang einen Kontakt des Anrufs nicht, bietet die Antwort
 * die Rückfrage „mit aufnehmen?" an (F2) — app-weit über den Boten.
 *
 * Fehler werden durchgereicht: Der Dialog bleibt offen und sagt es.
 */
export async function weiseZu(call: CallLogDto, ziele: CallZuordnungZiel[]): Promise<CallLogDto> {
    const next = await telephonyService.addZuordnungen(call.id, ziele, { resolve: !call.resolvedAt });
    kontaktAbgleichAnbieten(next.fehlendeKontakte);
    return next;
}

/** Eine Zuordnung lösen; andere bleiben. */
export function loese(call: Pick<CallLogDto, "id">, zuordnungId: number): Promise<CallLogDto> {
    return telephonyService.removeZuordnung(call.id, zuordnungId);
}

/**
 * Die Meldung des Servers aus einem `ok()`-Fehler („… fehlgeschlagen (400):
 * {"message":"…"}") — für Regeln wie „Vorgang hat To-dos – bitte ein To-do
 * wählen", die der Nutzer lesen soll. Sonst `null`.
 */
export function serverMeldung(error: unknown): string | null {
    const text = (error as Error | null)?.message ?? "";
    const json = text.slice(text.indexOf("{"));
    if (!json.startsWith("{")) return null;
    try {
        const m = (JSON.parse(json) as { message?: unknown }).message;
        if (typeof m === "string" && m.trim()) return m.trim();
        if (Array.isArray(m) && typeof m[0] === "string") return m[0];
    } catch {
        /* kein JSON */
    }
    return null;
}

/**
 * Position für das Knopf-Menü: linksbündig unter dem Knopf.
 *
 * `RdContextMenu` erwartet Viewport-Koordinaten (es rendert `position: fixed`)
 * und klemmt selbst an den Rändern — hier muss also nur der Anker stimmen.
 */
export function menuAnkerUnter(el: HTMLElement | null): { x: number; y: number } | null {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.bottom + 6 };
}

/**
 * Texte des Knopfes „Vorgang ▾" (PO 01.10.2026). Hängt der Anruf schon an
 * Vorgängen, nennt der Knopf die Zahl — und beide Wege FÜGEN HINZU (F1):
 * „Neuer Vorgang (zusätzlich)", „Weiterem Vorgang zuordnen…". Ersetzen heißt:
 * die alte Zuordnung im Anruf-Detail per × lösen.
 */
export function vorgangKnopfTexte(anzahl: number): {
    titel: string;
    neu: string;
    zuweisen: string;
} {
    if (!(anzahl > 0)) {
        return { titel: "Vorgang", neu: "Neuer Vorgang", zuweisen: "Bestehendem Vorgang zuweisen" };
    }
    return {
        titel: `Vorgang (${anzahl})`,
        neu: "Neuer Vorgang (zusätzlich)",
        zuweisen: "Weiterem Vorgang zuordnen…",
    };
}
